import { Formik, Form } from 'formik';
import { observer } from 'mobx-react-lite';
import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import CommonButton from '../../../../../../../components/common-components/button/CommonButton';
import Popup from '../../../../../../../components/common-components/pop-up/Popup';
import { Hash } from 'lucide-react';
import footerStyles from '../../../../../../../components/form-components/common-components/FormPopupFooter.module.css';
import { FormikTextField } from '../../../../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../../../../components/form-components/common-components/dropdown/FormikDropdown';
import { useStore } from '../../../../../../../hooks/useStore';
import QrOriginalData from '../qr-code/parts/QrOriginalData';
import styles from './NewRowFromKn.module.css';
import { hospitalRecordFieldIcons as HRI } from '../../../hospitalRecordFieldIcons';

const validationSchema = Yup.object({
  knNumber: Yup.string().required('Künye numarası gereklidir'),
});
const formValidationSchema = Yup.object({
  labelNo: Yup.number(),
  deviceDefinition: Yup.string(),
  brand: Yup.string(),
  model: Yup.string(),
  serialNumber: Yup.string(),
  tagNumber: Yup.string(),
  location: Yup.string(),
  owner: Yup.string(),
  notes: Yup.string(),
  color: Yup.string().required('Etiket rengi gereklidir'),
});
const NewRowFromKn = () => {
  const { popupStore, hospitalFilesStore, authStore } = useStore();
  const [formData, setFormData] = useState<any>(null);
  const [formHtmlString, setFormHtmlString] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isSearchSubmitting, setIsSearchSubmitting] = useState(false);
  const formikRef = useRef<any>(null);
  const searchFormikRef = useRef<any>(null);

  const searchByKn = async (knNumber: string) => {
    try {
      setLoading(true);
      setFormData(null);
      const response: any = await hospitalFilesStore.readFromKn(knNumber);
      if (response.value) {
        setFormHtmlString(response.value.rawResponse);
        const savedTagColor = localStorage.getItem('selectedTagColor') || 'red';
        const getNextLabelForColor = (color: string): number | '' => {
          const colorItem = hospitalFilesStore.tagColors?.find(
            x => x.color?.toLowerCase() === color?.toLowerCase()
          );
          if (colorItem && typeof colorItem.sequence === 'number') {
            return colorItem.sequence;
          }
          return '';
        };
        const newFormData = {
          labelNo: getNextLabelForColor(savedTagColor),
          deviceDefinition: response.value.hospitalRecord.biomedicalDeviceDescription || '',
          brand: response.value.hospitalRecord.brand || '',
          model: response.value.hospitalRecord.model || '',
          serialNumber: response.value.hospitalRecord.serialNumber || '',
          tagNumber: response.value.hospitalRecord.identificationNumber || '',
          location: response.value.hospitalRecord.location || '',
          owner: response.value.hospitalRecord.responsiblePerson || '',
          notes: response.value.hospitalRecord.note || '',
          color: savedTagColor, // localStorage'dan al veya varsayılan kırmızı
        };
        setFormData(newFormData);

        if (formikRef.current) {
          formikRef.current.resetForm();
          formikRef.current.setValues(newFormData);
        }
      } else {
        toast.error('Künye numarası ile kayıt bulunamadı');
      }
    } catch (error) {
      toast.error('Arama sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values: { knNumber: string }) => {
    searchByKn(values.knNumber);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (!hospitalFilesStore.selectedHospital) {
        toast.error('Lütfen bir hastane seçiniz');
        return;
      }

      if (!authStore.user) {
        toast.error('Kullanıcı bilgisi bulunamadı');
        return;
      }

      const newRecord = {
        tagId: hospitalFilesStore.selectedTag?.id,
        tagSequence: Number(values.labelNo ?? 0),
        biomedicalDeviceDescription: values.deviceDefinition,
        brand: values.brand,
        model: values.model,
        serialNumber: values.serialNumber,
        identificationNumber: values.tagNumber,
        location: values.location,
        responsiblePerson: values.owner,
        note: values.notes,
        hospitalId: hospitalFilesStore.selectedHospital.id,
        userId: authStore.user.id,
        color: values.color,
      };

      const response: any = await hospitalFilesStore.newRowManuel(newRecord);
      if (!response.error) {
        popupStore.setIsNewKnRowOpen(false);
        hospitalFilesStore.refreshSelectedTag();
        toast.success('Kayıt başarıyla eklendi');
      } else {
        toast.error(response.message || 'Kayıt Eklenemedi');
      }
    } catch (error) {
      console.error('Form gönderim hatası:', error);
      toast.error('Kayıt eklenirken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsNewKnRowOpen(false);
    setFormData(null);
  };

  const handleSuggestionSearch = async (fieldName: string, searchTerm: string) => {
    return hospitalFilesStore.searchFieldSuggestions(fieldName, searchTerm);
  };

  const handleFormSubmit = () => {
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  const handleSearchSubmit = () => {
    if (searchFormikRef.current) {
      searchFormikRef.current.submitForm();
    }
  };

  return (
    <div>
      <Popup
        isOpen={popupStore.isNewKnRowOpen}
        onClose={handleClose}
        title="Künye Numarası ile Veri Girişi"
        description="Künye numarası ile veri arayın ve kaydedin"
        variant="success"
        icon={<Hash size={22} strokeWidth={2} />}
        footer={
          formData ? (
            <div className={footerStyles.actions}>
              <CommonButton
                type="primary"
                className={footerStyles.saveBtn}
                disabled={isFormSubmitting}
                loading={isFormSubmitting}
                onClick={handleFormSubmit}
              >
                Kaydet
              </CommonButton>
            </div>
          ) : (
            <div className={footerStyles.actions}>
              <CommonButton
                type="primary"
                className={footerStyles.saveBtn}
                loading={loading || isSearchSubmitting}
                disabled={loading || isSearchSubmitting}
                onClick={handleSearchSubmit}
              >
                Ara
              </CommonButton>
            </div>
          )
        }
      >
        <div className={styles.container}>
          <div className={styles.searchContainer}>
            <Formik
              innerRef={searchFormikRef}
              initialValues={{ knNumber: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSearch}
            >
              {({ isSubmitting }) => {
                if (isSubmitting !== isSearchSubmitting) {
                  setIsSearchSubmitting(isSubmitting);
                }
                return (
                  <Form className={styles.searchForm}>
                    <FormikTextField
                      name="knNumber"
                      label="Künye Numarası"
                      placeholder="Künye numarası giriniz"
                      startIcon={HRI.tagNumber}
                    />
                  </Form>
                );
              }}
            </Formik>
          </div>

          <div className={styles.formContainer}>
            {formData && (
              <>
                <div style={{ marginBottom: '10px' }}>
                  <CommonButton
                    type="primary"
                    onClick={() => popupStore.setIsQrCodeOriginalDataOpen(true)}
                  >
                    Orijinal Veriyi Göster
                  </CommonButton>
                </div>
                <Formik
                  innerRef={formikRef}
                  initialValues={formData}
                  validationSchema={formValidationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ setFieldValue, isSubmitting }) => {
                    // Formik'in isSubmitting state'ini senkronize et
                    if (isSubmitting !== isFormSubmitting) {
                      setIsFormSubmitting(isSubmitting);
                    }
                    return (
                      <Form className={styles.form}>
                        <div className={styles.inputContainer}>
                          <FormikDropdown
                            name="color"
                            label="Etiket Rengi"
                            placeholder="Etiket rengi seçiniz"
                            startIcon={HRI.color}
                            options={[
                              { value: 'red', label: 'Kırmızı' },
                              { value: 'yellow', label: 'Sarı' },
                              { value: 'green', label: 'Yeşil' },
                            ]}
                            onChange={(value: string | number) => {
                              const v = (value as string) || 'red';
                              const colorItem = hospitalFilesStore.tagColors?.find(
                                x => x.color?.toLowerCase() === v.toLowerCase()
                              );
                              const next =
                                colorItem && typeof colorItem.sequence === 'number'
                                  ? colorItem.sequence + 1
                                  : '';
                              setFieldValue('labelNo', next);
                              localStorage.setItem('selectedTagColor', v);
                            }}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField
                            name="labelNo"
                            label="Etiket No"
                            type="number"
                            startIcon={HRI.labelNo}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField
                            name="deviceDefinition"
                            label="Biyomedikal Cihaz Tanımı"
                            fieldToSearch="biomedicalDeviceDescription"
                            onSuggestionSearch={handleSuggestionSearch}
                            startIcon={HRI.deviceDefinition}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField
                            name="brand"
                            label="Marka"
                            fieldToSearch="brand"
                            onSuggestionSearch={handleSuggestionSearch}
                            startIcon={HRI.brand}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField
                            name="model"
                            label="Model"
                            fieldToSearch="model"
                            onSuggestionSearch={handleSuggestionSearch}
                            startIcon={HRI.model}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField
                            name="serialNumber"
                            label="Seri Numarası"
                            startIcon={HRI.serialNumber}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField
                            name="tagNumber"
                            label="Künye Numarası"
                            startIcon={HRI.tagNumber}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField
                            name="location"
                            label="Bulunduğu Yer"
                            fieldToSearch="location"
                            onSuggestionSearch={handleSuggestionSearch}
                            startIcon={HRI.location}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField
                            name="owner"
                            label="Zimmet Sahibi"
                            fieldToSearch="responsiblePerson"
                            onSuggestionSearch={handleSuggestionSearch}
                            startIcon={HRI.owner}
                          />
                        </div>
                        <div className={styles.inputContainer}>
                          <FormikTextField name="notes" label="Not" startIcon={HRI.notes} />
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </>
            )}
          </div>
        </div>
      </Popup>
      {popupStore.isQrCodeOriginalDataOpen && <QrOriginalData data={formHtmlString} />}
    </div>
  );
};

export default observer(NewRowFromKn);
